import { useRef, useState, useMemo, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';

import {
  Button,
  LoadingOverlay,
} from '@finpay-development/shared-components';
import {
  Box,
  Typography,
  Grid,
  Paper,
} from '@mui/material';

import { formatNumberToUSD } from '../../shared/calculators';
import { RootState } from '../../shared/state/root-reducer';
import { usePaymentHubData } from '../hooks/use-payment-hub-data';
import RecurringPaymentMethodSection, { RecurringPaymentMethodSectionRef } from './recurring-payment-method-section';
import { updateProgramSchedule } from '../services/program-service';
import { showErrorStatus, showStatus } from '../../security/state/user-slice';
import { filterBankAccounts, deleteAndRefetchPaymentMethods, createFundingSourceRemovedNote, createFundingSourceSetAsRecurringNote } from '../hub-utils';
import { DeletePaymentMethodWarningModal } from './delete-payment-method-warning-modal';

// Workflow ID 3 = Converted
const WORKFLOW_ID_CONVERTED = 3;

const RecurringPaymentMethodView = () => {
  const {
    patientId,
    patientEncounterId,
    isLoading,
    authToken,
    payorsWithStatus,
    patientName,
    programId,
    pendingBalance,
    existingRecurringPaymentMethodId,
    isActiveRecurringProgram,
    selectedPayorId,
    selectedPayorUserLookupState,
    setSelectedPayorUserLookupState,
    selectedPayorBillingDetails,
    setSelectedPayorBillingDetails,
    selectedEncounter,
    navigate,
    dispatch,
  } = usePaymentHubData({ 
    guarantorsOnly: true, 
    autoSelectFirstPayor: true,
  });

  const [isProcessing, setIsProcessing] = useState(false);
  const [deletingMethodId, setDeletingMethodId] = useState<number | null>(null);
  const [showDeleteWarningModal, setShowDeleteWarningModal] = useState(false);
  const [pendingDeleteMethodId, setPendingDeleteMethodId] = useState<number | null>(null);
  const recurringPaymentMethodSectionRef = useRef<RecurringPaymentMethodSectionRef>(null);
  const userEmail = useSelector((state: RootState) => state.userContext.userProfile.userName);

  const executeDelete = useCallback(async (methodId: number) => {
    const userId = selectedPayorUserLookupState.userId;
    if (!userId || deletingMethodId) return;

    const methodToDelete = selectedPayorUserLookupState.savedPaymentMethods.find(
      (method) => method.userPaymentMethodId === methodId
    );
    const last4 = methodToDelete?.last4 || '****';

    setDeletingMethodId(methodId);
    try {
      const result = await deleteAndRefetchPaymentMethods({
        userId: parseInt(userId, 10),
        userPaymentMethodId: methodId,
        authToken,
      });
      if (result.success) {
        setSelectedPayorUserLookupState(prev => ({
          ...prev,
          savedPaymentMethods: result.paymentMethods,
        }));

        if (patientId && patientEncounterId && userEmail) {
          await createFundingSourceRemovedNote(dispatch, {
            patientId: parseInt(patientId, 10),
            patientEncounterId: parseInt(patientEncounterId, 10),
            last4,
            removedByEmail: userEmail,
          });
        }
      } else {
        dispatch(showErrorStatus(result.message));
      }
    } finally {
      setDeletingMethodId(null);
    }
  }, [selectedPayorUserLookupState, deletingMethodId, authToken, dispatch, setSelectedPayorUserLookupState, patientId, patientEncounterId, userEmail]);

  const handleDeleteMethod = useCallback(async (methodId: number) => {
    if (isActiveRecurringProgram && methodId === existingRecurringPaymentMethodId) {
      setPendingDeleteMethodId(methodId);
      setShowDeleteWarningModal(true);
    } else {
      await executeDelete(methodId);
    }
  }, [isActiveRecurringProgram, existingRecurringPaymentMethodId, executeDelete]);

  const handleConfirmDelete = useCallback(async () => {
    setShowDeleteWarningModal(false);
    if (pendingDeleteMethodId) {
      await executeDelete(pendingDeleteMethodId);
      setPendingDeleteMethodId(null);
    }
  }, [pendingDeleteMethodId, executeDelete]);

  // Determine the initial payment source (CARD or ACH) based on the existing recurring payment method
  const initialPaymentSource = useMemo((): 'CARD' | 'ACH' => {
    if (!existingRecurringPaymentMethodId || !selectedPayorUserLookupState.savedPaymentMethods.length) {
      return 'CARD';
    }
    
    // Check if the existing method is an ACH (bank account)
    const bankAccounts = filterBankAccounts(selectedPayorUserLookupState.savedPaymentMethods);
    const isAchMethod = bankAccounts.some(m => m.userPaymentMethodId === existingRecurringPaymentMethodId);
    
    return isAchMethod ? 'ACH' : 'CARD';
  }, [existingRecurringPaymentMethodId, selectedPayorUserLookupState.savedPaymentMethods]);

  const handleSubmit = async () => {
    if (!selectedPayorId) {
      dispatch(showErrorStatus('Please select a guarantor'));
      return;
    }

    if (!programId) {
      dispatch(showErrorStatus('No payment program found for this encounter'));
      return;
    }

    const recurringRef = recurringPaymentMethodSectionRef.current;
    if (!recurringRef) {
      dispatch(showErrorStatus('Payment method section not initialized'));
      return;
    }

    const isAddingNew = recurringRef.isAddingNew();
    const selectedMethodId = recurringRef.getSelectedMethodId();

    if (!isAddingNew && !selectedMethodId) {
      dispatch(showErrorStatus('Please select or add a payment method'));
      return;
    }

    const selectedGuarantor = payorsWithStatus.find(g => g.id?.toString() === selectedPayorId);
    if (!selectedGuarantor?.userId) {
      dispatch(showErrorStatus('Selected guarantor does not have a user account'));
      return;
    }

    setIsProcessing(true);

    try {
      let recurringMethodId: number | null = selectedMethodId;
      let selectedMethodLast4 = '****';

      if (isAddingNew) {
        const result = await recurringRef.createAndSavePaymentMethod({
          userId: selectedGuarantor.userId,
          authToken,
        });

        if (!result.success) {
          dispatch(showErrorStatus(result.error || 'Failed to create payment method'));
          setIsProcessing(false);
          return;
        }

        recurringMethodId = result.paymentMethodId;
        selectedMethodLast4 = result.last4 || '****';
      } else if (selectedMethodId) {
        const selectedMethod = selectedPayorUserLookupState.savedPaymentMethods.find(
          (method) => method.userPaymentMethodId === selectedMethodId
        );
        selectedMethodLast4 = selectedMethod?.last4 || '****';
      }

      if (!recurringMethodId) {
        dispatch(showErrorStatus('Failed to determine payment method'));
        setIsProcessing(false);
        return;
      }

      const scheduleResult = await updateProgramSchedule({
        programId,
        patientEncounterId: parseInt(patientEncounterId!, 10),
        recurringUserPaymentMethodId: recurringMethodId,
        authToken,
      });

      if (scheduleResult.success) {
        const recurringMethodChanged = recurringMethodId !== existingRecurringPaymentMethodId;

        if (recurringMethodChanged && patientId && patientEncounterId && userEmail) {
          await createFundingSourceSetAsRecurringNote(dispatch, {
            patientId: parseInt(patientId, 10),
            patientEncounterId: parseInt(patientEncounterId, 10),
            last4: selectedMethodLast4,
            setByEmail: userEmail,
          });
        }

        dispatch(showStatus('Recurring payment method updated successfully'));
        navigate(`/specialist/dashboard/${patientId}?encounter=${patientEncounterId}`);
      } else {
        dispatch(showErrorStatus(scheduleResult.message || 'Failed to update recurring payment method'));
      }
    } catch (error: any) {
      console.error('Error updating recurring payment method:', error);
      dispatch(showErrorStatus(error?.message || 'Failed to update recurring payment method'));
    } finally {
      setIsProcessing(false);
    }
  };

  // Redirect to /hub if no payment program exists and encounter is not converted (workflow ID !== 3)
  const workflowId = selectedEncounter?.workflow?.workflowId;
  const isConverted = workflowId === WORKFLOW_ID_CONVERTED;
  const hasEncounterLoaded = selectedEncounter?.patientEncounterId > 0;

  useEffect(() => {
    // Wait until loading is complete AND encounter data is loaded before deciding to redirect
    if (!isLoading && hasEncounterLoaded && !programId && patientId && patientEncounterId && !isConverted) {
      dispatch(showErrorStatus('No existing payment program or recurring payment method'));
      navigate(`/hub/patient/${patientId}/ioc/${patientEncounterId}`);
    }
  }, [isLoading, hasEncounterLoaded, programId, patientId, patientEncounterId, isConverted, dispatch, navigate]);

  if (isLoading) {
    return (
      <main test-id="recurring-payment-method-view">
        <div className="content px-4 py-4">
          <LoadingOverlay />
        </div>
      </main>
    );
  }

  // Don't render if no program and not converted (will redirect to /program)
  if (!programId && !isConverted) {
    return (
      <main test-id="recurring-payment-method-view">
        <div className="content px-4 py-4">
          <LoadingOverlay />
        </div>
      </main>
    );
  }

  return (
    <main test-id="recurring-payment-method-view">
      <div className="content px-4 py-4">
        <Paper elevation={2} sx={{ p: 4, maxWidth: 950, mx: 'auto' }} test-id="recurring-payment-method-form-paper">
          <Typography variant="h5" component="h1" align="center" gutterBottom fontWeight="bold" sx={{ mb: 3 }}>
            Set Recurring Payment Method
          </Typography>

          <Grid container spacing={2.5}>
            {/* Patient Name */}
            <Grid item xs={6}>
              <Typography variant="subtitle1" color="text.secondary">
                Patient Name
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body1" align="right" test-id="recurring-pm-patient-name">
                {patientName}
              </Typography>
            </Grid>

            {/* Pending Remaining Balance */}
            <Grid item xs={6} sx={{ display: 'flex', alignItems: 'center', py: 0.5 }}>
              <Typography variant="subtitle1" color="text.secondary">
                Pending Remaining Balance
              </Typography>
            </Grid>
            <Grid item xs={6} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', py: 0.5 }}>
              <Typography variant="body1" align="right" test-id="recurring-pm-pending-balance">
                {formatNumberToUSD(pendingBalance)}
              </Typography>
            </Grid>

            {/* Who is paying - display guarantor name */}
            <Grid item xs={6}>
              <Typography variant="subtitle1" color="text.secondary">
                Who is paying?
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography 
                variant="body1" 
                align="right" 
                test-id="recurring-pm-guarantor-name"
                sx={{ color: 'green', fontWeight: 'bold' }}
              >
                {payorsWithStatus.find(g => g.id?.toString() === selectedPayorId)?.name || 'Loading...'}
              </Typography>
            </Grid>

            {/* Recurring Payment Method Section */}
            {selectedPayorId && (
              <RecurringPaymentMethodSection
                ref={recurringPaymentMethodSectionRef}
                savedPaymentMethods={selectedPayorUserLookupState.savedPaymentMethods}
                billingDetails={selectedPayorBillingDetails}
                onBillingDetailsChange={(field: string, value: string) => {
                  setSelectedPayorBillingDetails(prev => ({ ...prev, [field]: value }));
                }}
                isAddingNewChampion={false}
                showAddressFields={false}
                showEmailField={false}
                initialSelectedMethodId={existingRecurringPaymentMethodId}
                initialPaymentSource={initialPaymentSource}
                onDeleteMethod={handleDeleteMethod}
                deletingMethodId={deletingMethodId}
              />
            )}

            {/* Action Buttons */}
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Box display="flex" justifyContent="flex-end" gap={2}>
                <Button
                  type="secondary"
                  onClick={() => navigate(`/specialist/dashboard/${patientId}?encounter=${patientEncounterId}`)}
                  disabled={isProcessing}
                  test-id="recurring-pm-cancel-button"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSubmit}
                  disabled={isProcessing || !selectedPayorId}
                  loading={isProcessing}
                  test-id="recurring-pm-submit-button"
                >
                  Save Recurring Payment Method
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </div>

      <DeletePaymentMethodWarningModal
        isOpen={showDeleteWarningModal}
        onCancel={() => {
          setShowDeleteWarningModal(false);
          setPendingDeleteMethodId(null);
        }}
        onConfirm={handleConfirmDelete}
      />
    </main>
  );
};

export default RecurringPaymentMethodView;
